import { useState } from 'react';
import { toast } from 'sonner';
import { History, PenLine, Play, Sparkles, Undo2 } from 'lucide-react';

import {
  FLAG_LABELS,
  processorName,
  signalName,
  type AssetSignalRow,
  useAssetSignalsQuery,
  useOverrideSignalMutation,
  useRunSignalsMutation,
  useSignalHistoryQuery,
} from '@/client/api/assets/signals';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const History_ = ({ asid, signalKey }: { asid: number; signalKey: string }) => {
  const { data, isLoading } = useSignalHistoryQuery(asid, signalKey, true);
  if (isLoading) return <Skeleton className="h-10 w-full" />;
  const rows = data?.history ?? [];
  if (!rows.length) return <p className="text-xs text-gray-400">No earlier values recorded.</p>;
  return (
    <ul className="space-y-1 text-xs text-gray-600">
      {rows.map((h, i) => (
        <li key={i} className="flex gap-2">
          <span className="w-36 shrink-0 text-gray-400">{h.date_added ? new Date(h.date_added).toLocaleString() : '—'}</span>
          <span>{h.label ?? h.content?.slice(0, 60) ?? '—'}</span>
          {h.value != null && <span className="tabular-nums">{h.value}</span>}
          <span className="text-gray-400">v{h.processor_version}</span>
          {h.shadow === 1 && <span className="text-purple-600">shadow</span>}
        </li>
      ))}
    </ul>
  );
};

/**
 * Every signal on one asset, re-runs and reviewer overrides. Re-runs queue the
 * same task an upload does; vision is billed per call, so it needs
 * asset.run.paid and an explicit confirm. Overrides survive re-runs.
 */
const AssetSignalsPanel = ({ asset }: { asset: Asset }) => {
  const asid = asset.asid!;
  const { data, isLoading, isError, error } = useAssetSignalsQuery(asid);
  const run = useRunSignalsMutation();
  const override = useOverrideSignalMutation();
  const { hasPermission: canRun, isSuperAdmin } = useHasPermission('asset.run');
  const { hasPermission: canRunPaid } = useHasPermission('asset.run.paid');
  const { hasPermission: canModerate } = useHasPermission('asset.moderate');

  const [paidConfirm, setPaidConfirm] = useState<string[] | null>(null);
  const [editing, setEditing] = useState<AssetSignalRow | null>(null);
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [openHistory, setOpenHistory] = useState<string | null>(null);

  const isImage = asset.kind ? asset.kind === 'image' : asset.mime_type?.startsWith('image/');
  if (!isImage) return null;

  const queue = async (processors: string[], confirm_paid = false) => {
    try {
      await run.mutateAsync({ asid, processors, confirm_paid });
      toast.success('Analysis queued. Values update in a minute or so; if nothing changes, the processor is switched off on the workers.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not queue the analysis'));
    }
  };

  const saveOverride = async (clear = false) => {
    if (!editing || !reason.trim()) return;
    const v = value.trim() === '' ? null : Number(value);
    if (!clear && v != null && (Number.isNaN(v) || v < 0 || v > 100)) {
      toast.error('Value must be 0–100');
      return;
    }
    try {
      await override.mutateAsync({ asid, key: editing.signal_key, value: clear ? null : v, label: clear ? null : label.trim() || null, reason: reason.trim(), clear });
      toast.success(clear ? 'Override cleared' : 'Override saved');
      setEditing(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save the override'));
    }
  };

  const openEdit = (s: AssetSignalRow) => {
    setEditing(s);
    setLabel(s.override?.label ?? '');
    setValue(s.override?.value != null ? String(s.override.value) : '');
    setReason('');
  };

  const runnable = (data?.processors ?? []).filter((p) => p.runnable && p.active_keys.length);

  return (
    <div>
      <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <Sparkles className="h-4 w-4" /> Signals
      </h4>

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : isError ? (
        <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Could not load signals')}</p>
      ) : (
        <>
          {(canRun || isSuperAdmin) && runnable.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {runnable.map((p) => (
                <Button
                  key={p.processor}
                  size="sm"
                  variant="outline"
                  disabled={run.isPending || (p.paid && !canRunPaid && !isSuperAdmin)}
                  title={p.paid && !canRunPaid && !isSuperAdmin ? 'Paid analysis needs asset.run.paid' : undefined}
                  onClick={() => (p.paid ? setPaidConfirm([p.processor]) : queue([p.processor]))}
                >
                  <Play className="mr-1.5 h-3.5 w-3.5" /> Re-run {processorName(p.processor)}
                  {p.paid && <span className="ml-1 text-[10px] text-purple-600">paid</span>}
                </Button>
              ))}
            </div>
          )}

          {!data?.signals.length ? (
            <p className="text-sm text-gray-500">No analysis on this photo yet.</p>
          ) : (
            <ul className="divide-y rounded-md border text-sm">
              {data.signals.map((s) => {
                const list = Array.isArray(s.content_json) ? (s.content_json as unknown[]).map(String).join(', ') : null;
                const shown = s.effective_label ?? (s.content ? s.content.slice(0, 120) : list || (s.effective_value != null ? String(s.effective_value) : '—'));
                return (
                  <li key={s.signal_key} className="px-3 py-2">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-gray-500">{signalName(s.signal_key)}</div>
                        <div className={cn('break-words', s.effective_label && FLAG_LABELS.has(s.effective_label) && 'font-medium text-amber-700')}>
                          {shown}
                          {s.effective_value != null && s.effective_label && (
                            <span className="ml-2 text-xs tabular-nums text-gray-400">{s.effective_value}</span>
                          )}
                        </div>
                        {s.override && (
                          <div className="mt-0.5 text-[11px] text-blue-700">
                            Overridden (was {s.label ?? s.value ?? '—'}){s.override.reason ? ` — ${s.override.reason}` : ''}
                          </div>
                        )}
                      </div>
                      {s.history_count > 1 && (
                        <button className="text-gray-400 hover:text-gray-700" title="History"
                          onClick={() => setOpenHistory(openHistory === s.signal_key ? null : s.signal_key)}>
                          <History className="h-4 w-4" />
                        </button>
                      )}
                      {(canModerate || isSuperAdmin) && (s.label != null || s.value != null) && (
                        <button className="text-gray-400 hover:text-gray-700" title="Override" onClick={() => openEdit(s)}>
                          <PenLine className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {openHistory === s.signal_key && (
                      <div className="mt-2 rounded bg-gray-50 p-2">
                        <History_ asid={asid} signalKey={s.signal_key} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <AlertDialog open={!!paidConfirm} onOpenChange={(o) => !o && setPaidConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Run paid analysis?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends asset #{asid} to the vision model: one billed call. It replaces the current values (the old ones stay in
              history) and leaves reviewer overrides in place.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                const procs = paidConfirm ?? [];
                setPaidConfirm(null);
                void queue(procs, true);
              }}
            >
              Run (billed)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Override {editing ? signalName(editing.signal_key) : ''}</AlertDialogTitle>
            <AlertDialogDescription>
              The analysis said {editing?.label ?? '—'}{editing?.value != null ? ` (${editing.value})` : ''}. Your value wins
              everywhere signals are read, including photo checks, and survives re-runs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input placeholder="Label, e.g. clean" value={label} onChange={(e) => setLabel(e.target.value)} />
            <Input placeholder="Score 0–100 (optional)" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
          <Input placeholder="Reason (required, kept in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <AlertDialogFooter>
            {editing?.override && (
              <Button variant="ghost" disabled={!reason.trim() || override.isPending} onClick={() => void saveOverride(true)}>
                <Undo2 className="mr-1.5 h-4 w-4" /> Clear override
              </Button>
            )}
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!reason.trim() || (!label.trim() && value.trim() === '') || override.isPending}
              onClick={(e) => {
                e.preventDefault();
                void saveOverride(false);
              }}
            >
              Save override
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AssetSignalsPanel;
